import React, { useState, useEffect } from "react";
import { variantSelect, productSelect } from "@/store";
import {
  usePdpActions,
  usePdpSellingPlan,
  usePdpProduct,
  usePdpVariant,
} from "./PdpContext";
import { Button } from "@/components/Button";
import { CartAddLineItem, shopifyCartApi } from "@/services/shopifyCart";
import { useCartUiActions } from "../cart";
import {
  SubscriptionSelector,
  SubscriptionSelectorValue,
} from "@/features/product/SubscriptionSelector";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import { AddToCartProps, VariantSizeOptionsGroup } from "./AddToCart";

export const AddToCartProductCard = ({
  cartSubmitId,
  addToCartText = "Add to Cart",
  outOfStockText = "Out of Stock",
  onAddToCartSuccess,
}: AddToCartProps) => {
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();
  const variant = usePdpVariant();
  const actions = usePdpActions();
  const subscription = variantSelect.orderableSubscription(variant);
  const product = usePdpProduct();
  const sellingPlan = usePdpSellingPlan();

  const isBundle =
    product?.tags?.includes("bundle") &&
    !product.handle.includes("starter-kit");

  // State for subscription toggle
  const [subToggle, setSubToggle] = useState<SubscriptionSelectorValue>(
    sellingPlan ? "subscription" : "one-time"
  );

  const subDiscount = variantSelect.subscriptionDiscount(variant) ?? 0;

  // Calculate the subscription price
  const subscriptionPrice =
    variantSelect.orderableSubscription(variant)?.discount.price;

  // Define the displayed price based on subscription toggle
  const displayedPrice =
    subToggle === "subscription" && subscriptionPrice
      ? subscriptionPrice
      : variant.price;

  const addToCartPrice = (
    <>
      {variant?.price != undefined ? (
        <div className="flex flex-wrap items-center space-x-2 lg:justify-end">
          <p
            data-id="product-card-price"
            className={`body ml-1 text-xs font-semibold text-white ${
              variant?.size || variant?.subscription ? "order-2" : ""
            }`}
          >
            <span className="sr-only">Current price</span>
            {displayedPrice?.format()}
          </p>
        </div>
      ) : null}
    </>
  );

  // Update subscription preference when subToggle changes
  useEffect(() => {
    actions.setSubscriptionPreference(subToggle === "subscription");
  }, [subToggle, actions]);

  return (
    <>
      {(variant?.size || variant?.subscription || variant?.price) && (
        <div className="plp-box-sizes_variant-options mt-4">
          <VariantSizeOptionsGroup />
        </div>
      )}

      <div className="w-full">
        {subscription && (
          <div className="pt-3">
            <SubscriptionSelector
              value={subToggle}
              onChange={(value) => {
                setSubToggle(value);
              }}
              size="sm"
              cta={
                <p className="body w-full text-center text-xs">
                  <span>
                    {subDiscount ? (
                      <>Save {subDiscount}% when you subscribe.</>
                    ) : null}{" "}
                    Ships every 4 months.
                  </span>
                </p>
              }
            />
          </div>
        )}

        <div className="mt-4 flex w-full flex-col-reverse items-center justify-between space-x-0 lg:mt-1 lg:space-y-2">
          <Button
            className="learn-more-card-link mt-1 items-center space-x-2"
            component="a"
            size="sm"
            variant="ghost"
            href={`/products/${product.handle}?variant=${variant.id}`}
            aria-label={`Learn more about ${product.title}`}
          >
            <InfoOutlined className="h-4 w-4 text-inherit" />{" "}
            <span>More details</span>
          </Button>
          <Button
            id={cartSubmitId}
            className="lg:btn-sm xl:btn-base btn-base w-full"
            disabled={!variant.available}
            onClick={() => {
              const lineItem: CartAddLineItem[] = [
                variantSelect.cartAddLineItem(variant, {
                  subscription:
                    subToggle === "subscription"
                      ? productSelect.defaultSellingPlan(product)
                      : false,
                }),
              ];
              const sourceModule = isBundle
                ? "bundle_plp_quick_add"
                : "plp_quick_add";
              const body = { items: lineItem, sourceModule };
              shopifyCartAdder.mutate(body, {
                onSuccess: () => {
                  cartActions.openDrawer();
                  if (onAddToCartSuccess) {
                    onAddToCartSuccess();
                  }
                },
              });
            }}
          >
            <div className="flex w-full items-center justify-between text-xs">
              <span>{variant.available ? addToCartText : outOfStockText}</span>
              {addToCartPrice}
            </div>
          </Button>
        </div>
        {!variant.available && (
          <div className="basis-full pt-4">
            <Button
              id="BIS_trigger"
              className="flex w-full"
              aria-label="notify me when restocked"
              size="lg"
            >
              Notify Me When Restocked
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
